import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import {
  ButtonContainer,
  InvisibleInput,
  MobileUploadDiv,
  ModalError,
  StyledLabel,
  StyledUploadDiv,
  TextWrapper,
} from './styled';
import { NavigationAndActionUpload } from '../../ui/icons';
import { DocumentationPathsEnum } from '../routes';
import { AllowedDocumentFormats, Category } from './helpers';
import { DocumentationSelectors } from '../redux';
import { useAppDispatch, useAppSelector } from '../../hooks/appHooks';
import { BodyText, Button, ButtonType, Loader, PageWithHeader } from '../../ui';
import { useFileManagement } from '../../hooks/useFileManagement';
import { UploadedFilesList } from './common/UploadedFilesList';
import { DocumentDetailsForm } from './common/DocumentDetailsForm';
import { uploadFinished } from '../redux/slice';

interface LocationState {
  category: Category;
}

export const UploadPageMobile: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const location = useLocation<LocationState>();
  const [errorMessage, setErrorMessage] = useState('');
  const { displayName, documentCategory } = location.state.category;
  const formRef = useRef<HTMLFormElement>();

  const { files, setFiles, addFile, removeFile, handleUpload } =
    useFileManagement(setErrorMessage);

  const uploadSuccess = useAppSelector(
    DocumentationSelectors.selectUploadSuccess,
  );
  const uploadError = useAppSelector(DocumentationSelectors.selectUploadError);
  const uploadSubmitActive = useAppSelector(
    DocumentationSelectors.selectUploadLoading,
  );

  const closeModal = useCallback(() => {
    setFiles([]);
    history.push(DocumentationPathsEnum.documentationPage);
  }, [history, setFiles]);

  useEffect(() => {
    if (uploadError) {
      setErrorMessage(uploadError);
    }
    if (uploadSuccess) {
      closeModal();
    }
  }, [uploadSuccess, uploadError, closeModal]);

  const handleUploadButtonClick = () => {
    formRef.current?.dispatchEvent(
      new Event('submit', { cancelable: true, bubbles: true }),
    );

    if (!files.length) {
      setErrorMessage(t('documentationPage.emptyFileListMessage'));
    }
  };

  useEffect(
    () => () => {
      dispatch(uploadFinished());
    },
    [dispatch],
  );

  return (
    <PageWithHeader
      breadcrumbs={[
        { text: t('documentationPage.breadcrumbs.home'), url: '/' },
        {
          text: t('documentationPage.breadcrumbs.documentation'),
          url: DocumentationPathsEnum.documentationPage,
        },
        { text: t('documentationPage.breadcrumbs.upload'), url: '' },
      ]}
      title={t(displayName)}>
      <Loader active={uploadSubmitActive}>
        <DocumentDetailsForm
          documentCategory={documentCategory}
          uploadedFilesExists={files.length > 0}
          uploadDocument={handleUpload}
          formRef={formRef}
        />
        <MobileUploadDiv>
          <StyledLabel>
            {t('documentationPage.uploadInstructionMobile')}
          </StyledLabel>
          <TextWrapper>
            <BodyText>{t('documentationPage.uploadAdditionalText')}</BodyText>
          </TextWrapper>
          <label htmlFor="file_click">
            <StyledUploadDiv>
              <NavigationAndActionUpload />
              <p>{t('documentationPage.uploadButtonText')}</p>
            </StyledUploadDiv>
            <InvisibleInput
              type="file"
              id="file_click"
              accept={AllowedDocumentFormats}
              multiple
              onInput={(e) => addFile(e)}
            />
          </label>
        </MobileUploadDiv>
      </Loader>
      <ModalError hasError={!!errorMessage}>
        <p>{errorMessage}</p>
      </ModalError>
      <UploadedFilesList files={files} removeFile={removeFile} />
      <ButtonContainer>
        <Button
          text={t('documentationPage.add')}
          onClick={handleUploadButtonClick}
        />
        <Button
          type={ButtonType.Secondary}
          text={t('documentationPage.cancel')}
          onClick={() => history.push(DocumentationPathsEnum.documentationPage)}
        />
      </ButtonContainer>
    </PageWithHeader>
  );
};
